import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";

export type LegalKind = "privacy" | "terms";

export const LegalPage = ({ kind }: { kind: LegalKind }) => {
  const { t, lang } = usePrefs();
  const nav = useNavigate();
  const loc = useLocation();
  const [d, setD] = useState<{ text_ar: string; text_en: string; at?: string } | null>(null);
  useEffect(() => { api.get(`/platform/legal/${kind}`).then((r) => setD(r.data)).catch(() => setD({ text_ar: "", text_en: "" })); }, [kind]);
  const text = d ? (lang === "en" && d.text_en) || d.text_ar : "";
  return (
    <div data-testid={`legal-page-${kind}`} className="min-h-screen bg-background px-5 py-6 max-w-2xl mx-auto space-y-6 animate-in fade-in duration-500">
      <button onClick={() => (loc.key === "default" ? nav("/") : nav(-1))} data-testid="legal-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground">
        <ArrowRight className="h-4 w-4 ltr:rotate-180" />{t("common.back")}
      </button>
      <h1 className="text-4xl font-black">{t(`legal.${kind}`)}</h1>
      {d?.at && <p className="text-xs text-muted-foreground">{t("legal.updated")} {new Date(d.at).toLocaleDateString(lang === "ar" ? "ar-IQ" : "en-GB")}</p>}
      {d === null ? <div className="h-40 rounded-3xl bg-muted animate-pulse" /> : (
        <article data-testid="legal-text" className="rounded-3xl border bg-card p-5 leading-8 whitespace-pre-line break-words">{text || t("legal.empty")}</article>
      )}
    </div>
  );
};

export const LegalLinks = ({ className = "" }: { className?: string }) => {
  const { t } = usePrefs();
  return (
    <p className={`flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-muted-foreground ${className}`}>
      <Link to="/terms" data-testid="terms-link" className="hover:text-foreground underline-offset-4 hover:underline">{t("legal.terms")}</Link>
      <Link to="/privacy" data-testid="privacy-link" className="hover:text-foreground underline-offset-4 hover:underline">{t("legal.privacy")}</Link>
    </p>
  );
};

export const AcceptTerms = ({ checked, onChange, className = "" }: { checked: boolean; onChange: (v: boolean) => void; className?: string }) => {
  const { t } = usePrefs();
  return (
    <label data-testid="accept-terms" className={`flex items-start gap-2 text-sm ${className}`}>
      <input type="checkbox" required checked={checked} onChange={(e) => { e.target.setCustomValidity(""); onChange(e.target.checked); }}
        onInvalid={(e) => (e.target as HTMLInputElement).setCustomValidity(t("err.You must accept the terms and privacy policy"))} data-testid="accept-terms-checkbox" className="mt-1 h-4 w-4 accent-[hsl(var(--primary))]" />
      <span>
        {t("legal.agree1")} <Link to="/terms" target="_blank" className="font-bold text-primary">{t("legal.terms")}</Link> {t("legal.and")} <Link to="/privacy" target="_blank" className="font-bold text-primary">{t("legal.privacy")}</Link>
      </span>
    </label>
  );
};

export const LegalSettings = () => {
  const { t } = usePrefs();
  const [kind, setKind] = useState<LegalKind>("terms");
  const [f, setF] = useState({ text_ar: "", text_en: "" });
  useEffect(() => {
    let live = true;
    setF({ text_ar: "", text_en: "" });
    api.get(`/platform/legal/${kind}`).then((r) => live && setF({ text_ar: r.data.text_ar, text_en: r.data.text_en })).catch(() => {});
    return () => { live = false; };
  }, [kind]);
  const save = async () => {
    try { await api.put(`/admin/platform/legal/${kind}`, f); toast.success(t("legal.saved")); } catch (e) { toast.error(errorText(e, t)); }
  };
  return (
    <div data-testid="legal-settings" className="rounded-3xl border bg-card p-5 space-y-3">
      <p className="font-bold">{t("legal.adminTitle")}</p>
      <div className="flex gap-2">
        {(["terms", "privacy"] as LegalKind[]).map((k) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`legal-tab-${k}`} className={`h-9 px-4 rounded-full text-sm font-bold ${kind === k ? "bg-foreground text-background" : "border"}`}>{t(`legal.${k}`)}</button>
        ))}
      </div>
      <textarea dir="rtl" rows={8} maxLength={20000} value={f.text_ar} onChange={(e) => setF({ ...f, text_ar: e.target.value })} data-testid="legal-text-ar-input" placeholder={t("about.textAr")} className="w-full rounded-2xl border bg-background p-3 text-sm" />
      <textarea dir="ltr" rows={5} maxLength={20000} value={f.text_en} onChange={(e) => setF({ ...f, text_en: e.target.value })} data-testid="legal-text-en-input" placeholder={t("about.textEn")} className="w-full rounded-2xl border bg-background p-3 text-sm" />
      <button onClick={save} data-testid="legal-save" className="h-10 px-5 rounded-full bg-primary text-primary-foreground text-sm font-bold active:scale-95 transition-transform">{t("common.save")}</button>
    </div>
  );
};
